import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Loader2, Plus, Power, Trash2 } from "lucide-react";
import type { ApiClientDto, ApiClientWithSecret } from "@ambulance/shared";
import { useApi } from "../api";
import { apiUrl, oidcConfig } from "../auth";
import { useMe } from "../hooks/useMe";
import { btnPrimary, btnSecondary, ErrorBox, Field, Forbidden, inputCls, Modal, Spinner } from "../components/ui";

function CopyButton({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      <Copy className="w-3.5 h-3.5" /> {done ? "Copied" : "Copy"}
    </button>
  );
}

/** Super admin: identities for external systems (OAuth2 client credentials, read-only). */
export function ApiClientsPage() {
  const { me, isLoading } = useMe();
  const api = useApi();
  const qc = useQueryClient();
  const clients = useQuery({ queryKey: ["api-clients"], queryFn: () => api.get<ApiClientDto[]>("/api/v1/api-clients"), enabled: Boolean(api.token) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["api-clients"] });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [limit, setLimit] = useState(120);
  const [secretFor, setSecretFor] = useState<ApiClientWithSecret | null>(null);

  const create = useMutation({ mutationFn: () => api.post<ApiClientWithSecret>("/api/v1/api-clients", { name, rateLimitPerMin: limit }), onSuccess: (c) => { invalidate(); setShowCreate(false); setName(""); setSecretFor(c); } });
  const rotate = useMutation({ mutationFn: (id: string) => api.post<ApiClientWithSecret>(`/api/v1/api-clients/${id}/rotate-secret`), onSuccess: (c) => setSecretFor(c) });
  const toggle = useMutation({ mutationFn: (c: ApiClientDto) => api.patch<ApiClientDto>(`/api/v1/api-clients/${c.id}`, { active: !c.active }), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.del<void>(`/api/v1/api-clients/${id}`), onSuccess: invalidate });

  if (isLoading) return <Spinner />;
  if (!me || me.role !== "super_admin") return <Forbidden />;

  const tokenUrl = `${oidcConfig.authority}/protocol/openid-connect/token`;
  const list = clients.data ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">API Clients</h1>
        <button type="button" onClick={() => setShowCreate(true)} className={btnPrimary}><Plus className="w-4 h-4" /> New API client</button>
      </div>
      <p className="text-sm text-slate-600 mb-6">External systems authenticate with OAuth2 client credentials and get read-only access to hospitals, ambulances, units, checklists and bag checklists of this tenant. Every call is written to the audit log. Disabling or deleting a client revokes access immediately.</p>

      {clients.isPending ? (
        <Spinner />
      ) : clients.isError ? (
        <ErrorBox>{clients.error.message}</ErrorBox>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">No API clients yet.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">client_id</th>
                  <th className="px-4 py-2">Limit / min</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-2 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{c.clientId} <CopyButton value={c.clientId} /></td>
                    <td className="px-4 py-2">{c.rateLimitPerMin}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded ${c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{c.active ? "Active" : "Disabled"}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        <button type="button" title="Rotate secret" className="p-1.5 rounded hover:bg-slate-100 text-slate-600" onClick={() => { if (confirm("Rotate the secret? The old secret stops working immediately.")) rotate.mutate(c.id); }}><KeyRound className="w-4 h-4" /></button>
                        <button type="button" title={c.active ? "Disable" : "Enable"} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" onClick={() => toggle.mutate(c)}><Power className="w-4 h-4" /></button>
                        <button type="button" title="Delete" className="p-1.5 rounded hover:bg-red-50 text-red-600" onClick={() => { if (confirm(`Delete API client "${c.name}"? Its tokens stop working immediately.`)) remove.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {(rotate.isError || toggle.isError || remove.isError) && <div className="mt-3"><ErrorBox>{(rotate.error ?? toggle.error ?? remove.error)?.message}</ErrorBox></div>}

      {showCreate && (
        <Modal title="New API client" subtitle="A Keycloak client with a service account is created for this tenant." onClose={() => setShowCreate(false)}>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="System name"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fleet Dashboard" required minLength={2} /></Field>
            <Field label="Requests per minute"><input className={inputCls} type="number" min={10} max={5000} value={limit} onChange={(e) => setLimit(Number(e.target.value))} /></Field>
            {create.isError && <ErrorBox>{create.error.message}</ErrorBox>}
            <div className="flex justify-end gap-2">
              <button type="button" className={btnSecondary} onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" className={btnPrimary} disabled={create.isPending}>{create.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Create</button>
            </div>
          </form>
        </Modal>
      )}

      {secretFor && (
        <Modal title="Client credentials" subtitle="Copy the secret now. It is shown only once." onClose={() => setSecretFor(null)} size="lg">
          <div className="space-y-3 text-sm">
            <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">client_id</div><div className="font-mono bg-slate-50 border border-slate-200 rounded p-2 break-all">{secretFor.clientId} <CopyButton value={secretFor.clientId} /></div></div>
            <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">client_secret</div><div className="font-mono bg-slate-50 border border-slate-200 rounded p-2 break-all">{secretFor.secret} <CopyButton value={secretFor.secret} /></div></div>
            <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Get a token</div>
              <pre className="bg-slate-900 text-slate-100 rounded p-3 text-xs overflow-x-auto">{`curl -X POST ${tokenUrl} \\
  -d grant_type=client_credentials \\
  -d client_id=${secretFor.clientId} \\
  -d client_secret=YOUR_SECRET`}</pre>
            </div>
            <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Call the API</div>
              <pre className="bg-slate-900 text-slate-100 rounded p-3 text-xs overflow-x-auto">{`curl ${apiUrl}/api/v1/checklists?limit=50 \\
  -H "Authorization: Bearer ACCESS_TOKEN"`}</pre>
            </div>
            <div className="flex justify-end"><button type="button" className={btnPrimary} onClick={() => setSecretFor(null)}>Done</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
